import { employeeAdvanceSalaryResponseType } from 'components/EmployeeOnboarding/EmployeeAdvanceSalary/types';
import {
  AllowanceForSalaryModalEmployeeOnboardingAnnualCompensationType,
  AnnualEmployeeCompensationAllowancePropertiesType,
  AnnualEmployeeCompensationPerquisitesType,
  DeductionForSalaryModalEmployeeOnboardingAnnualCompensationType,
  EmployeeOnboardingAnnualCompensationDataType,
} from 'components/EmployeeOnboarding/EmployeeAnnualCompensation/types';
import { RunPayrollData } from 'components/RunPayroll/utils/types';
import { SuccessResponseSchemaContract } from 'schemas/SuccessResponseSchema';
import { getApi, postApi, patchApi } from 'utils/Api';
import getBasicInfoResponseSchema, {
  employeeIdAvailabilityResponseSchema,
  generatedEmailResponseSchema,
  updateBasicInfoResponseSchema,
} from 'schemas/BasicInfoSchema';
import {
  GetBasicInfoResponse,
  SalaryPreviewResponse,
  UpdateBasicInfoPayload,
  UpdateBasicInfoResponse,
  VerifyPanPayload,
  VerifyPanResponse,
} from './types/employeeOnboardingTypes';

export type EmployeeOnBoardingFetchAnnualCompensationResponse = {
  data: EmployeeOnboardingAnnualCompensationDataType;
};

export type EmployeeOnBoardingUpdateAnnualCompensationResponse = {
  success: boolean;
  errorMessage: string | string[];
};

export type SalaryRevisionValidationResponse = {
  annualSalary: number;
  arrears:
    | null
    | {
        name: string;
        amount: number;
        type: string;
      }[];
  arrearMonth: string;
  salaryModelValidation: string[];
  existingArrearsDistribution?: RunPayrollData['arrearsDistribution'];
};

export type EmployeeAnnualyCompensationUpdateRequest = {
  isCustomSalaryStructure: boolean;
  basicSalary: number;
  da: number;
  hra: number;
  specialAllowance: number;
  lta: number;
  npsEmployerContributionLimit: number;
  vpf: number;
  annualSalary: number;
  variablePay: number;
  isEligibleForEmployerESIContribution: boolean;
  employerESIContribution: number;
  isEligibleForEmployerPFContribution: boolean;
  employerPFContribution: number;
  flexiPartnerAllowanceAmount: number;
  allowance: AllowanceForSalaryModalEmployeeOnboardingAnnualCompensationType[];
  deductions: DeductionForSalaryModalEmployeeOnboardingAnnualCompensationType[];
  perquisites: AnnualEmployeeCompensationPerquisitesType | null;
  effectiveFrom: string;
};

export type EmployeeAdvanceSalaryRequestType = {
  advanceSalaryEMI: number;
  advanceSalary: number;
};

export type PFRegistrationStatus =
  | 'Enabled'
  | 'Disabled'
  | 'Company Disabled'
  | 'Registration Pending'
  | 'Registration Not Initiated'
  | 'Registration Failed';

export type ESICRegistrationStatus =
  | 'Enabled'
  | 'Disabled'
  | 'Company Disabled'
  | 'Registration Pending'
  | 'Registration Not Initiated'
  | 'Registration Failed';

export type PFWageCalculationType = 'default' | '15k' | 'no_limit';

export type EmployeeStatutorySettingsResponse = {
  tds_settings: {
    is_tds_enabled?: boolean | null;
    aadhaar_number: string;
    pan: string;
  };
  pf_settings: {
    is_pf_enabled: boolean | null;
    pf_status: string;
    pf_uan: string;
    is_aadhaar_linked_to_uan: boolean;
    include_employer_contribution_in_ctc: boolean;
    contribute_to_eps: boolean;
    pf_registration_status: PFRegistrationStatus;
    pf_wages_calculation_type?: PFWageCalculationType;
  };
  esic_settings: {
    is_esic_enabled: boolean;
    esic_status: string;
    esic_ip_number: string;
    esic_registration_status: ESICRegistrationStatus;
  };
  pt_settings: {
    is_pt_enabled: boolean | null;
    pt_state: number | string | null;
  };
  lwf_settings: {
    is_lwf_enabled: boolean;
    lwf_location: number | string | null;
  };
  nps_settings: {
    is_nps_enabled: boolean;
    nps_pran_number: string;
  };
  pt_eligible_states: Array<number> | null;
  meta_data?: {
    employment_type?: 'employee' | 'contractor' | null;
  };
};

export type EmployeeStatutorySettingsUpdateRequest = Omit<
  EmployeeStatutorySettingsResponse,
  'pf_settings' | 'esic_settings' | 'pt_eligible_states' | 'meta_data'
> & {
  pf_settings: Omit<EmployeeStatutorySettingsResponse['pf_settings'], 'pf_registration_status'>;
  esic_settings: Omit<
    EmployeeStatutorySettingsResponse['esic_settings'],
    'esic_registration_status'
  >;
};

export const VERIFY_PAN = 'VERIFY_PAN';

const employeeOnboarding = {
  fetchEmployeeAnnualyCompensation: (employeeId: number) => {
    return getApi<EmployeeOnBoardingFetchAnnualCompensationResponse>({
      url: `/employeeOnboarding/${employeeId}/employeeAnnualCompensation`,
      responseSchema: {},
    });
  },

  updateEmployeeAnnualyCompensation: (
    employeeId: number,
    {
      isCustomSalaryStructure,
      basicSalary,
      da,
      hra,
      specialAllowance,
      lta,
      npsEmployerContributionLimit,
      vpf,
      annualSalary,
      variablePay,
      isEligibleForEmployerESIContribution,
      employerESIContribution,
      isEligibleForEmployerPFContribution,
      employerPFContribution,
      flexiPartnerAllowanceAmount,
      allowance,
      deductions,
      perquisites,
      effectiveFrom,
    }: EmployeeAnnualyCompensationUpdateRequest,
  ) => {
    return postApi<SuccessResponseSchemaContract, {}>({
      url: `/employeeOnboarding/${employeeId}/employeeAnnualCompensation`,
      responseSchema: {},
      requestData: {
        isCustomSalaryStructure: isCustomSalaryStructure,
        basicSalary: basicSalary,
        da: da,
        hra: hra,
        specialAllowance: specialAllowance,
        lta: lta,
        npsEmployerContributionLimit: npsEmployerContributionLimit,
        vpf: vpf,
        annualSalary: annualSalary,
        variablePay: variablePay,
        isEligibleForEmployerESIContribution: isEligibleForEmployerESIContribution,
        employerESIContribution: employerESIContribution,
        isEligibleForEmployerPFContribution: isEligibleForEmployerPFContribution,
        employerPFContribution: employerPFContribution,
        flexiPartnerAllowanceAmount: flexiPartnerAllowanceAmount,
        allowance: allowance,
        deductions: deductions,
        perquisites: perquisites,
        effectiveFrom,
      },
    });
  },

  validateEmployeeAnnualyCompensation: (
    employeeId: number,
    {
      isCustomSalaryStructure,
      basicSalary,
      da,
      hra,
      specialAllowance,
      lta,
      npsEmployerContributionLimit,
      vpf,
      annualSalary,
      variablePay,
      isEligibleForEmployerESIContribution,
      employerESIContribution,
      isEligibleForEmployerPFContribution,
      employerPFContribution,
      flexiPartnerAllowanceAmount,
      allowance,
      deductions,
      effectiveFrom,
    }: EmployeeAnnualyCompensationUpdateRequest,
  ) => {
    return postApi<SalaryRevisionValidationResponse, {}>({
      url: `/employeeOnboarding/${employeeId}/employeeAnnualCompensation/validate`,
      responseSchema: {},
      requestData: {
        isCustomSalaryStructure: isCustomSalaryStructure,
        basicSalary: basicSalary,
        da: da,
        hra: hra,
        specialAllowance: specialAllowance,
        lta: lta,
        npsEmployerContributionLimit: npsEmployerContributionLimit,
        vpf: vpf,
        annualSalary: annualSalary,
        variablePay: variablePay,
        isEligibleForEmployerESIContribution: isEligibleForEmployerESIContribution,
        employerESIContribution: employerESIContribution,
        isEligibleForEmployerPFContribution: isEligibleForEmployerPFContribution,
        employerPFContribution: employerPFContribution,
        flexiPartnerAllowanceAmount: flexiPartnerAllowanceAmount,
        allowance: allowance,
        deductions: deductions,
        effectiveFrom,
      },
    });
  },

  fetchAdvanceSalary: (employeeId: number) => {
    return getApi<employeeAdvanceSalaryResponseType>({
      url: `/employeeOnboarding/${employeeId}/fetchAdvanceSalary`,
      responseSchema: {},
    });
  },

  updateAdvanceSalary: (
    employeeId: number,
    { advanceSalaryEMI, advanceSalary }: EmployeeAdvanceSalaryRequestType,
  ) => {
    return postApi<SuccessResponseSchemaContract, {}>({
      url: `/employeeOnboarding/${employeeId}/updateAdvanceSalary`,
      responseSchema: {},
      requestData: {
        advanceSalaryEMI: advanceSalaryEMI,
        advanceSalary: advanceSalary,
      },
    });
  },

  fetchEmployeeStatutorySettings: (employeeId: number) => {
    return getApi<EmployeeStatutorySettingsResponse>({
      url: `people/${employeeId}/employee-statutory-settings`,
      responseSchema: {},
    });
  },

  updateEmployeeStatutorySettings: (
    employeeId: number,
    settings: EmployeeStatutorySettingsUpdateRequest,
  ) => {
    return postApi<SuccessResponseSchemaContract, {}>({
      url: `people/${employeeId}/employee-statutory-settings`,
      responseSchema: {},
      requestData: settings,
    });
  },
  verifyPan: (payload: VerifyPanPayload) => {
    return postApi<VerifyPanResponse, VerifyPanPayload>({
      url: '/pan-verification/verify',
      requestData: payload,
      responseSchema: {},
    });
  },
  fetchSalaryPreview: () => {
    return getApi<SalaryPreviewResponse>({
      url: 'salary-preview',
      responseSchema: {},
    });
  },

  getBasicInfo: (userId: number) => {
    return getApi<GetBasicInfoResponse>({
      url: `people/${userId}/basic-info`,
      responseSchema: getBasicInfoResponseSchema,
    });
  },

  updateBasicInfo: (userId: number, data: UpdateBasicInfoPayload) => {
    return patchApi<UpdateBasicInfoResponse, UpdateBasicInfoPayload>({
      url: `people/${userId}/basic-info`,
      responseSchema: updateBasicInfoResponseSchema,
      requestData: data,
    });
  },

  /**
   * The live Employee ID check the legacy Basic Information form runs as you type
   * (OrganizationController::validateEmployeeIdAvailability). Keyed by the
   * people_organization id, not the people id.
   */
  checkEmployeeIdAvailability: (peopleOrgId: number, employeeId: string, employeeType: string) => {
    const query = new URLSearchParams({ employeeId, employeeType }).toString();
    return getApi<{ isAvailable: boolean }>({
      url: `validate-employee-id-availability/${peopleOrgId}?${query}`,
      responseSchema: employeeIdAvailabilityResponseSchema,
    });
  },

  /**
   * The org's next auto-generated email address, from the V1 endpoint the legacy
   * form's "click here to generate one" link calls (api/generateEmailAddress.php).
   * It lives outside /v2/api, so the URL is absolute.
   */
  generateEmailAddress: (employeeType: string) => {
    const query = new URLSearchParams({ employeeType }).toString();
    return getApi<{ email: string }>({
      url: `${window.location.origin}/api/generateEmailAddress?${query}`,
      responseSchema: generatedEmailResponseSchema,
    });
  },
};

export default employeeOnboarding;
